<template>
  <div class="card">
    <div class="pb-0 card-header mb-0">
      <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <h5 class="mb-1">Ventas por mes</h5>
          <p class="text-sm mb-0">
            <span class="font-weight-bold">{{ summaryText }}</span>
          </p>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <select v-model.number="selectedYear" class="form-select form-select-sm" style="width: auto; min-width: 6rem;">
            <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
          </select>
          <select v-model="syncFilter" class="form-select form-select-sm" style="width: auto; min-width: 11rem;">
            <option value="all">Todas las ventas</option>
            <option value="siigo">Sincronizadas (Siigo)</option>
            <option value="local">Sin sincronizar</option>
          </select>
        </div>
      </div>
    </div>
    <div class="p-3 card-body">
      <div v-if="loading" class="text-center py-2 text-muted text-sm">Cargando ventas...</div>
      <div class="chart" style="height: 320px;">
        <canvas ref="salesChart" class="chart-canvas" height="300"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "sales-year-chart",
  data() {
    const currentYear = new Date().getFullYear();
    return {
      selectedYear: currentYear,
      syncFilter: "all",
      yearOptions: Array.from({ length: 6 }, (_, i) => currentYear - i),
      monthsData: [],
      totals: {},
      loading: false,
      chart: null,
    };
  },
  computed: {
    summaryText() {
      if (!this.totals || Object.keys(this.totals).length === 0) {
        return `Año ${this.selectedYear}`;
      }

      if (this.syncFilter === "siigo") {
        return `${this.formatMoney(this.totals.total)} · ${this.totals.count} ventas sincronizadas`;
      }

      if (this.syncFilter === "local") {
        return `${this.formatMoney(this.totals.total)} · ${this.totals.count} ventas sin sincronizar`;
      }

      return `${this.formatMoney(this.totals.total)} total · ${this.totals.synced_count} sincronizadas (${this.formatMoney(this.totals.synced_total)}) · ${this.totals.local_count} locales (${this.formatMoney(this.totals.local_total)})`;
    },
  },
  watch: {
    selectedYear() {
      this.fetchSalesData();
    },
    syncFilter() {
      this.fetchSalesData();
    },
  },
  methods: {
    formatMoney(value) {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(value) || 0);
    },
    async fetchSalesData() {
      this.loading = true;
      try {
        const response = await this.$store.dispatch("analitycs/getSalesByYear", {
          year: this.selectedYear,
          sync: this.syncFilter,
        });
        this.monthsData = response.data.months || [];
        this.totals = response.data.totals || {};
      } catch (error) {
        console.error("Error al cargar ventas por año:", error);
        this.monthsData = [];
        this.totals = {};
      } finally {
        this.loading = false;
        await this.$nextTick();
        this.updateChart();
      }
    },
    updateChart() {
      const canvas = this.$refs.salesChart;
      if (!canvas) return;

      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }

      const labels = this.monthsData.map((item) => item.month);
      const datasets = [];

      if (this.syncFilter === "all") {
        datasets.push(
          {
            label: "Sincronizadas (Siigo)",
            backgroundColor: "rgba(45, 206, 137, 0.65)",
            data: this.monthsData.map((item) => Number(item.synced_total) || 0),
            barThickness: 28,
          },
          {
            label: "Sin sincronizar",
            backgroundColor: "rgba(249, 176, 2, 0.65)",
            data: this.monthsData.map((item) => Number(item.local_total) || 0),
            barThickness: 28,
          }
        );
      } else {
        const label = this.syncFilter === "siigo" ? "Sincronizadas (Siigo)" : "Sin sincronizar";
        const color = this.syncFilter === "siigo" ? "rgba(45, 206, 137, 0.65)" : "rgba(249, 176, 2, 0.65)";
        datasets.push({
          label,
          backgroundColor: color,
          data: this.monthsData.map((item) => Number(item.total) || 0),
          barThickness: 40,
        });
      }

      const monthsData = this.monthsData;
      const formatMoney = this.formatMoney;

      this.chart = new Chart(canvas.getContext("2d"), {
        type: "bar",
        data: {
          labels,
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
            },
            tooltip: {
              callbacks: {
                label(context) {
                  const index = context.dataIndex;
                  const month = monthsData[index];
                  const lines = [`${context.dataset.label}: ${formatMoney(context.raw)}`];

                  if (month) {
                    if (context.dataset.label.includes("Sincronizadas")) {
                      lines.push(`Ventas: ${month.synced_count ?? month.count ?? 0}`);
                    } else if (context.dataset.label.includes("Sin sincronizar")) {
                      lines.push(`Ventas: ${month.local_count ?? month.count ?? 0}`);
                    } else {
                      lines.push(`Ventas: ${month.count ?? 0}`);
                    }
                  }

                  return lines;
                },
              },
            },
          },
          interaction: {
            intersect: true,
            mode: "nearest",
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: false,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                padding: 10,
                color: "#000",
                callback(value) {
                  return new Intl.NumberFormat("es-CO", {
                    notation: "compact",
                    maximumFractionDigits: 1,
                  }).format(value);
                },
                font: {
                  size: 12,
                  family: "Open Sans",
                },
              },
            },
            x: {
              grid: {
                drawBorder: false,
                display: false,
              },
              ticks: {
                display: true,
                color: "#000",
                padding: 12,
                font: {
                  size: 11,
                  family: "Open Sans",
                },
              },
              offset: true,
            },
          },
        },
      });
    },
  },
  mounted() {
    this.fetchSalesData();
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
};
</script>
